import { NS, type NamespaceTuple } from '@ownyou/shared-types';
import { createMissionSpineRunId, recordMissionSpineMarker } from '../mission-spine/instrumentation';
import { appendMissionReactionInboxEvent } from '../inbox/events';

export type MissionReaction = 'like' | 'dislike' | 'neutral';
export type MissionReactionSource = 'card' | 'detail' | 'triage' | 'legacy';
export type LegacyFeedbackSentiment = 'positive' | 'negative';
export type LegacyHeartState = 'meh' | 'like' | 'love';

export interface MissionReactionRecord {
  missionId: string;
  reaction: MissionReaction;
  source: MissionReactionSource;
  note?: string;
  createdAt: number;
  updatedAt: number;
}

export interface MissionReactionStore {
  get<T>(namespace: NamespaceTuple, key: string): Promise<T | null>;
  put<T>(namespace: NamespaceTuple, key: string, value: T): Promise<void>;
}

export interface MissionReactionListStore {
  list<T>(namespace: NamespaceTuple, options?: { limit?: number; offset?: number }): Promise<{ items: T[] }>;
}

export interface MissionFeedbackRankable {
  id: string;
  type?: string;
  tags?: string[];
  entitySource?: string;
  entityCategory?: string;
}

export interface ExactDislikedFilterResult<T extends MissionFeedbackRankable> {
  missions: T[];
  hiddenCount: number;
}

export interface FeedbackRankingSummary {
  reactionCount: number;
  boostedCount: number;
  downrankedCount: number;
}

export interface FeedbackRankingResult<T extends MissionFeedbackRankable> {
  missions: T[];
  summary: FeedbackRankingSummary;
}

export interface RecordMissionReactionInput {
  store: MissionReactionStore;
  userId: string;
  missionId: string;
  reaction: MissionReaction;
  source: MissionReactionSource;
  note?: string;
  now?: () => number;
  emitMarker?: boolean;
}

export function legacySentimentToReaction(sentiment: LegacyFeedbackSentiment): MissionReaction {
  return sentiment === 'positive' ? 'like' : 'dislike';
}

export function legacyHeartStateToReaction(state: LegacyHeartState): MissionReaction {
  if (state === 'like' || state === 'love') return 'like';
  return 'neutral';
}

export function isMissionReaction(value: unknown): value is MissionReaction {
  return value === 'like' || value === 'dislike' || value === 'neutral';
}

export async function listMissionReactions(
  store: MissionReactionListStore,
  userId: string
): Promise<MissionReactionRecord[]> {
  const result = await store.list<unknown>(NS.missionFeedback(userId), { limit: 1000, offset: 0 });
  return result.items.flatMap(coerceMissionReactionRecord);
}

export function mergeLegacyCardFeedbackReactions(
  reactions: MissionReactionRecord[],
  missions: MissionFeedbackRankable[]
): MissionReactionRecord[] {
  const byMissionId = new Map(reactions.map(reaction => [reaction.missionId, reaction]));

  for (const mission of missions) {
    if (byMissionId.has(mission.id)) continue;
    const raw = mission as unknown as Record<string, unknown>;
    const sentiment = raw.feedbackSentiment;
    if (sentiment !== 'positive' && sentiment !== 'negative') continue;
    byMissionId.set(mission.id, {
      missionId: mission.id,
      reaction: legacySentimentToReaction(sentiment),
      source: 'legacy',
      createdAt: numericTimestamp(raw.createdAt) ?? 0,
      updatedAt: numericTimestamp(raw.updatedAt) ?? numericTimestamp(raw.createdAt) ?? 0,
    });
  }

  return Array.from(byMissionId.values());
}

export function missionsAreFeedbackSimilar(
  reactedMission: MissionFeedbackRankable,
  candidateMission: MissionFeedbackRankable
): boolean {
  if (reactedMission.id === candidateMission.id) return false;

  const reactedSource = normalizeAttribute(reactedMission.entitySource);
  const candidateSource = normalizeAttribute(candidateMission.entitySource);
  if (reactedSource && candidateSource && reactedSource === candidateSource) return true;

  const reactedCategory = normalizeAttribute(reactedMission.entityCategory);
  const candidateCategory = normalizeAttribute(candidateMission.entityCategory);
  if (reactedCategory && candidateCategory && reactedCategory === candidateCategory) return true;

  const reactedType = normalizeAttribute(reactedMission.type);
  const candidateType = normalizeAttribute(candidateMission.type);
  if (!reactedType || !candidateType || reactedType !== candidateType) return false;

  const reactedTags = new Set(normalizedTags(reactedMission.tags));
  if (reactedTags.size === 0) return false;
  return normalizedTags(candidateMission.tags).some(tag => reactedTags.has(tag));
}

export function filterExactDislikedMissions<T extends MissionFeedbackRankable>(
  missions: T[],
  reactions: MissionReactionRecord[]
): ExactDislikedFilterResult<T> {
  const dislikedMissionIds = new Set(
    reactions
      .filter(reaction => reaction.reaction === 'dislike')
      .map(reaction => reaction.missionId)
  );

  const filtered = missions.filter(mission => !dislikedMissionIds.has(mission.id));
  return {
    missions: filtered,
    hiddenCount: missions.length - filtered.length,
  };
}

export function applyMissionFeedbackRanking<T extends MissionFeedbackRankable>(
  missions: T[],
  referenceMissions: MissionFeedbackRankable[],
  reactions: MissionReactionRecord[]
): FeedbackRankingResult<T> {
  const referenceById = new Map(referenceMissions.map(mission => [mission.id, mission]));
  const actionableReactions = reactions.filter(reaction => reaction.reaction === 'like' || reaction.reaction === 'dislike');
  let boostedCount = 0;
  let downrankedCount = 0;

  const tiered = missions.map((mission, index) => {
    const tier = feedbackTierForMission(mission, referenceById, actionableReactions);
    if (tier === 0) boostedCount += 1;
    if (tier === 2) downrankedCount += 1;
    return { mission, index, tier };
  });

  tiered.sort((a, b) => a.tier - b.tier || a.index - b.index);

  return {
    missions: tiered.map(item => item.mission),
    summary: {
      reactionCount: actionableReactions.length,
      boostedCount,
      downrankedCount,
    },
  };
}

export async function recordMissionReaction(input: RecordMissionReactionInput): Promise<MissionReactionRecord> {
  const timestamp = input.now?.() ?? Date.now();
  const namespace = NS.missionFeedback(input.userId);
  const existing = await input.store.get<MissionReactionRecord>(namespace, input.missionId);
  const note = normalizeNote(input.note);

  const record: MissionReactionRecord = {
    missionId: input.missionId,
    reaction: input.reaction,
    source: input.source,
    ...(note ? { note } : {}),
    createdAt: existing?.createdAt ?? timestamp,
    updatedAt: timestamp,
  };

  await input.store.put(namespace, input.missionId, record);
  await appendMissionReactionInboxEvent({
    store: input.store,
    userId: input.userId,
    missionId: input.missionId,
    reaction: record.reaction,
    createdAt: timestamp,
  }).catch((error) => {
    console.warn('[recordMissionReaction] Failed to append Inbox event:', error);
  });

  if (input.emitMarker !== false) {
    recordMissionSpineMarker({
      runId: createMissionSpineRunId('slice-03-reaction'),
      hop: 'mission_reaction_write',
      status: 'success',
      namespace: namespace[0],
      sourceId: input.missionId,
      reasonCode: `reaction_${record.reaction}`,
      counts: {
        notePresent: note ? 1 : 0,
      },
    });
  }

  return record;
}

function normalizeNote(note: string | undefined): string | undefined {
  const trimmed = note?.trim();
  return trimmed ? trimmed : undefined;
}

function feedbackTierForMission(
  mission: MissionFeedbackRankable,
  referenceById: Map<string, MissionFeedbackRankable>,
  reactions: MissionReactionRecord[]
): 0 | 1 | 2 {
  const exact = reactions.find(reaction => reaction.missionId === mission.id);
  if (exact?.reaction === 'like') return 0;

  let hasLikedSimilarity = false;
  for (const reaction of reactions) {
    const reactedMission = referenceById.get(reaction.missionId);
    if (!reactedMission || !missionsAreFeedbackSimilar(reactedMission, mission)) continue;
    if (reaction.reaction === 'dislike') return 2;
    if (reaction.reaction === 'like') hasLikedSimilarity = true;
  }

  return hasLikedSimilarity ? 0 : 1;
}

function coerceMissionReactionRecord(item: unknown): MissionReactionRecord[] {
  if (!item || typeof item !== 'object') return [];
  const raw = item as Record<string, unknown>;
  const missionId = typeof raw.missionId === 'string' ? raw.missionId : undefined;
  if (!missionId) return [];

  const timestamp = numericTimestamp(raw.updatedAt) ?? numericTimestamp(raw.createdAt) ?? numericTimestamp(raw.timestamp) ?? 0;
  const createdAt = numericTimestamp(raw.createdAt) ?? numericTimestamp(raw.timestamp) ?? timestamp;
  const source = isMissionReactionSource(raw.source) ? raw.source : 'legacy';
  const note = typeof raw.note === 'string' ? normalizeNote(raw.note) : undefined;

  if (isMissionReaction(raw.reaction)) {
    return [{
      missionId,
      reaction: raw.reaction,
      source,
      ...(note ? { note } : {}),
      createdAt,
      updatedAt: timestamp,
    }];
  }

  if (raw.state === 'meh' || raw.state === 'like' || raw.state === 'love') {
    return [{
      missionId,
      reaction: legacyHeartStateToReaction(raw.state),
      source: 'legacy',
      ...(note ? { note } : {}),
      createdAt,
      updatedAt: timestamp,
    }];
  }

  if (raw.feedbackSentiment === 'positive' || raw.feedbackSentiment === 'negative') {
    return [{
      missionId,
      reaction: legacySentimentToReaction(raw.feedbackSentiment),
      source: 'legacy',
      ...(note ? { note } : {}),
      createdAt,
      updatedAt: timestamp,
    }];
  }

  return [];
}

function isMissionReactionSource(value: unknown): value is MissionReactionSource {
  return value === 'card' || value === 'detail' || value === 'triage' || value === 'legacy';
}

function normalizeAttribute(value: unknown): string {
  return typeof value === 'string' ? value.trim().toLowerCase().replace(/\s+/g, ' ') : '';
}

function normalizedTags(tags: unknown): string[] {
  if (!Array.isArray(tags)) return [];
  return tags.flatMap(tag => {
    const normalized = normalizeAttribute(tag);
    return normalized ? [normalized] : [];
  });
}

function numericTimestamp(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}
